import React, { useState } from 'react';
import { IconMap } from './Icons';

export function SectionLabel({ text }) {
  return (
    <div style={styles.labelContainer}>
      <span style={styles.labelLine}></span>
      <div style={styles.label}>{text}</div>
    </div>
  );
}

export function SectionHeader({ labelText, title, description, lightMode = true }) {
  return (
    <div style={{ marginBottom: '56px' }}>
      <SectionLabel text={labelText} />
      <h2 style={{ ...styles.sectionTitle, color: lightMode ? '#0d1b2a' : '#ffffff' }}>
        {title}
      </h2>
      {description && (
        <p style={{ ...styles.sectionDesc, color: lightMode ? '#64748b' : '#94a3b8' }}>
          {description}
        </p>
      )}
    </div>
  );
}

export function InfoCard({ iconKey, iconBg, title, description }) {
  return (
    <div style={styles.card}>
      <div style={{ ...styles.iconWrapper, backgroundColor: iconBg }}>
        {IconMap[iconKey]}
      </div>
      <h3 style={styles.cardH3}>{title}</h3>
      <p style={styles.cardP}>{description}</p>
    </div>
  );
}

// 1. REUSABLE TIMELINE/STEP CARD CONTAINER
export function StepCard({ number, title, description, badgeText }) {
  return (
    <div style={styles.stepCard}>
      <div style={styles.stepHeader}>
        <div style={styles.stepNumber}>{number}</div>
        <span style={styles.stepBadge}>{badgeText}</span>
      </div>
      <h3 style={styles.cardH3}>{title}</h3>
      <p style={styles.cardP}>{description}</p>
    </div>
  );
}

// 2. INTERACTIVE ACCORDION MODULE FOR FAQ REDUCTION
export function AccordionItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div style={{...styles.accordionWrapper, borderBottom: isOpen ? '2px solid #d4ac0d' : '1px solid #e2e8f0'}}>
      <button onClick={() => setIsOpen(!isOpen)} style={styles.accordionHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', textAlign: 'left' }}>
          {IconMap.help}
          <span style={styles.accordionQuestion}>{question}</span>
        </div>
        <div style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
          {IconMap.chevronDown}
        </div>
      </button>
      {isOpen && <div style={styles.accordionContent}><p style={styles.cardP}>{answer}</p></div>}
    </div>
  );
}

// Add this component alongside your other SharedComponents
export function HowStepCard({ number, title, description }) {
  return (
    <div style={styles.howStep}>
      <div style={styles.stepCircle}>{number}</div>
      <h3 style={styles.howStepH3}>{title}</h3>
      <p style={styles.howStepP}>{description}</p>
    </div>
  );
}

// Add these exact style definitions to the bottom of the styles object in SharedComponents.jsx
const additionalStyles = {
  howStep: {
    backgroundColor: '#112235',
    borderRadius: '8px',
    padding: '40px 32px',
    border: '1px solid rgba(255,255,255,0.05)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    position: 'relative'
  },
  stepCircle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: '18px',
    fontWeight: '800',
    color: '#0d1b2a',
    backgroundColor: '#d4ac0d',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px'
  },
  howStepH3: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: '22px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#ffffff',
    marginBottom: '12px'
  },
  howStepP: {
    fontSize: '15px',
    color: '#94a3b8',
    lineHeight: '1.6',
    fontWeight: '400',
    margin: 0
  }
};

const styles = {
  labelContainer: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  labelLine: { width: '24px', height: '2px', backgroundColor: '#2e86c1' },
  label: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '20px', fontWeight: '700', letterSpacing: '3px', color: '#2e86c1' },
  sectionTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(32px, 4.5vw, 48px)', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '20px', lineHeight: '1.05', maxWidth: '800px' },
  sectionDesc: { fontSize: '18px', lineHeight: '1.65', maxWidth: '760px', fontWeight: '400' },
  card: { backgroundColor: '#ffffff', borderRadius: '8px', padding: '40px 36px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 10px 15px -3px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' },
  iconWrapper: { width: '56px', height: '56px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifycontent: 'center', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' },
  cardH3: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '22px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#0d1b2a', marginBottom: '12px' },
  cardP: { fontSize: '15px', color: '#64748b', lineHeight: '1.6', fontWeight: '400' },
  stepCard: { backgroundColor: '#112235', borderRadius: '8px', padding: '36px', border: '1px solid rgba(255,255,255,0.05)', position: 'relative' },
  stepHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  stepNumber: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '32px', fontWeight: '800', color: '#d4ac0d', opacity: 0.8 },
  stepBadge: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: '700', background: 'rgba(46, 134, 193, 0.2)', color: '#2e86c1', padding: '4px 10px', borderRadius: '4px', letterSpacing: '1px' },
  accordionWrapper: { backgroundColor: '#ffffff', borderRadius: '6px', marginBottom: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', transition: 'all 0.2s ease' },
  accordionHeader: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px', background: 'none', border: 'none', cursor: 'pointer', outline: 'none' },
  accordionQuestion: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '18px', fontWeight: '700', color: '#0d1b2a', textTransform: 'uppercase', letterSpacing: '0.3px' },
  accordionContent: { padding: '0 24px 24px 56px', backgroundColor: '#f8fafc' }
};